import { computed } from 'vue';
import { useQuery, queryOptions, useQueryClient } from '@tanstack/vue-query';
import { getWidgetConfig } from '@swapin-hub/api/config';
import { useI18n } from '@swapin-hub/i18n';

import { SERVICE } from '@/const';
import { useConfigStore } from '@/stores/config';

const getConfigQueryOptions = () => {
  const configStore = useConfigStore();

  const configKey = configStore.key;
  const configParams = Object.fromEntries(new URLSearchParams(configStore.url).entries());

  return queryOptions({
    queryKey: ['config', configKey, configParams],
    queryFn: () => getWidgetConfig(configKey, configParams),
  });
};

export const useConfig = ({ query = {} } = {}) => {
  const configQuery = useQuery({
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...query,
    ...getConfigQueryOptions(),
    meta: {
      persistOnLogout: true,
    },
  });

  const { data: config } = configQuery;

  const { t } = useI18n();

  const allServices = computed(() => {
    const servicesValues = Object.values(SERVICE);

    return Object.fromEntries(servicesValues.map((key) => [key, {
      key,
      label: config.value?.[`${key}ButtonTitle`] || t(`SERVICE.${key}`),
    }]));
  });

  const configServices = computed(() => {
    const servicesKeys = Object.keys((config.value?.services || {})).map((key) => key.toLowerCase());
    const filteredServices = servicesKeys.map((key) => allServices.value[key]);

    return Object.fromEntries(filteredServices.map((item) => [item.key, item]));
  });

  return {
    ...configQuery,
    allServices,
    configServices,
  };
};

export const getConfigQueryData = () => {
  const queryClient = useQueryClient();

  return queryClient.getQueryData(getConfigQueryOptions().queryKey);
};
